import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest, AssetAnalogParameter, AssetTemperatureParameter, IoTypeListItem } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { ComboField, EntityUtils, NumberField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { MeasurementUnitsService } from 'app/services';

interface EditorValues {
    analogs: ListFieldValue<AssetAnalogParameter>[];
    temperatures: ListFieldValue<AssetTemperatureParameter>[];
}

export class AssetAnalogEventsSectionHandler implements FormSectionHandler<AssetResponse, EditorValues> {

    type = 'form';
    icon = 'heart-rate';
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    properties = ['analogs', 'temperatures'];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public units: MeasurementUnitsService
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.ANALOG_EVENTS.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: AssetUpdateRequest): Promise<AssetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetResponse): Promise<EditorValues> {
        const result: EditorValues = {
            analogs: (entity.parameters && entity.parameters.analogs && entity.parameters.analogs.map(analog => this.analogToListItem(analog))) || [],
            temperatures: (entity.parameters && entity.parameters.temperatures && entity.parameters.temperatures.map(temp => {
                const minRange = this.units.fromBackend('temperature', temp.minRange, 1);
                const maxRange = this.units.fromBackend('temperature', temp.maxRange, 1);
                return this.temperatureToListItem({ ...temp, minRange, maxRange });
            })) || [],
        };
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: AssetUpdateRequest, values: EditorValues): Promise<AssetUpdateRequest> {
        entity.parameters = entity.parameters || {};
        if (values.analogs) {
            entity.parameters.analogs = values.analogs.map(x => x.data);
        }
        if (values.temperatures) {
            entity.parameters.temperatures = values.temperatures.map(x => {
                const minRange = this.units.toBackend('temperature', x.data.minRange, 1) as number;
                const maxRange = this.units.toBackend('temperature', x.data.maxRange, 1) as number;
                return { ...x.data, minRange, maxRange };
            });
        }
        return Promise.resolve(entity);
    }

    getFields(entity: AssetResponse): Promise<FormBuilderField[]> {
        return Promise.resolve([
            {
                id: 'analogs', title: this.translate('FIELDS.ANALOGS'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddAnalogModal.bind(this), },
                    { id: 'edit', click: this.showAddAnalogModal.bind(this), },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
            {
                id: 'temperatures', title: this.translate('FIELDS.TEMPERATURES'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddTemperatureModal.bind(this), },
                    { id: 'edit', click: this.showAddTemperatureModal.bind(this), },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ]).then(fields => EntityUtils.prepareBulk(!entity, ['analogs', 'temperatures'], fields));
    }

    mapValidationErrors(errors: { [field: string]: string }): { [field: string]: string } {

        const result = {};
        Object.keys(errors).forEach(field => {
            this.properties.forEach(prop => {
                if (field.startsWith(prop)) {
                    result[prop] = result[prop] || [];
                    result[prop].push(errors[field]);
                }
            });
        });
        return Object.keys(result).reduce((obj, key) => { obj[key] = result[key].join(', '); return obj; }, {});
    }

    analogToListItem(analog: AssetAnalogParameter): ListFieldValue<AssetAnalogParameter> {
        return {
            id: analog.id,
            name: analog.name,
            icon: 'heart-rate',
            description: this.translate('FIELDS.ANALOG_DESC', analog),
            data: analog,
        };
    }

    temperatureToListItem(temp: AssetTemperatureParameter): ListFieldValue<AssetTemperatureParameter> {
        return {
            id: temp.id,
            name: temp.name,
            icon: 'thermometer-half',
            description: this.translate('FIELDS.TEMPERATURE_DESC', temp),
            data: temp,
        };
    }

    updateOrAddItem<T extends { id: string, ioType: string }>(items: ListFieldValue<any>[], existingItem: ListFieldValue<any>, analog: T, itemToListItem: (analog: T) => ListFieldValue<any>) {

        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = itemToListItem(analog);
        } else {
            const idx = items.findIndex(x => x.data.id === analog.id);
            if (idx === -1) {
                items.push(itemToListItem(analog));
            } else {
                // we only allow one entry per type, so overwrite any existing entries
                items[idx] = itemToListItem(analog);
            }
        }
    }


    async getIoTypes(types: string[]): Promise<IoTypeListItem[]> {
        const typeFilters = types.map(t => `type=${t}`).join('|');
        const type = types.length > 1 ? `(${typeFilters})` : typeFilters;
        const filter = type ? `state=active,${type}` : `state=active`;
        const ioTypes = await this.admin.getCached(`ioTypes.${this.ownerId}.true.${filter}`, (api) => api.entities.listIoTypes(this.ownerId, true, 0, 300, 'name', filter), 15);
        return ioTypes.items;
    }


    showAddAnalogModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<AssetAnalogParameter> {

        return this.getIoTypes(['analog_input', 'can_input']).then(ioTypes => {

            return this.admin.showFormModal<AssetAnalogParameter>({
                groups: [{
                    name: this.translate('MODALS.ANALOG.TITLE'),
                    description: this.translate('MODALS.ANALOG.DESC'),
                    fields: [
                        ComboField('id', this.translate('FIELDS.ANALOG_INPUT'), true, EntityUtils.idNameToComboValues(ioTypes)),
                        NumberField('minRange', this.translate('FIELDS.MIN_RANGE'), true, -100000, 100000, undefined),
                        NumberField('maxRange', this.translate('FIELDS.MAX_RANGE'), true, -100000, 100000, undefined),
                        NumberField('rangeDebounce', this.translate('FIELDS.RANGE_DEBOUNCE'), true, 0, 10, undefined),
                        NumberField('decValue', this.translate('FIELDS.DROP_AMOUNT'), true, 0, 10000, undefined),
                        NumberField('incValue', this.translate('FIELDS.INCREASE_AMOUNT'), true, 0, 10000, undefined),
                    ],
                }],
            }, (item && item.data) || { // pass some defaults for new items
                minRange: 0,
                maxRange: 100,
                rangeDebounce: 0,
                decValue: 0,
                incValue: 0,
                ioType: 'analog_input',
            }
            ).then(analog => {
                if (analog) {
                    analog.name = ioTypes.find(x => x.id === analog.id).name;
                    this.updateOrAddItem(values.analogs, item, analog, this.analogToListItem.bind(this));
                }
                return analog;
            });

        });

    }

    showAddTemperatureModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<AssetTemperatureParameter> {
        const temperatureUnit = this.admin.units.format(0, 'temperature').unit;

        return this.getIoTypes(['temperature_input']).then(ioTypes => {

            return this.admin.showFormModal<AssetTemperatureParameter>({
                groups: [{
                    name: this.translate('MODALS.TEMPERATURE.TITLE'),
                    description: this.translate('MODALS.TEMPERATURE.DESC'),
                    fields: [
                        ComboField('id', this.translate('FIELDS.TEMPERATURE_INPUT'), true, EntityUtils.idNameToComboValues(ioTypes)),
                        NumberField('minRange', this.translate('FIELDS.MIN_RANGE'), true, -100000, 100000, temperatureUnit),
                        NumberField('maxRange', this.translate('FIELDS.MAX_RANGE'), true, -100000, 100000, temperatureUnit),
                        NumberField('rangeDebounce', this.translate('FIELDS.RANGE_DEBOUNCE'), true, 0, 10, undefined),
                    ],
                }],
            }, (item && item.data) || { // pass some defaults for new items
                minRange: 0,
                maxRange: 100,
                rangeDebounce: 0,
                ioType: 'temperature_input',
            }
            ).then(temp => {
                if (temp) {
                    temp.name = ioTypes.find(x => x.id === temp.id).name;
                    this.updateOrAddItem(values.temperatures, item, temp, this.temperatureToListItem.bind(this));
                }
                return temp;
            });

        });

    }


}

